/* eslint-disable jira/import-whitelist */
import { nav4SidebarGlobalProductsSettingsResource } from '@jira/router-resources__navigation-sidebar-global-products-settings/src/nav4SidebarGlobalProductsSettingsResource.tsx';
import { checkApiKeyManagementKeysResource } from '@jira/router-resources__eoc-api-key-management/src/checkApiKeyManagementKeysResource.tsx';
import { consolidationUserStateResource } from '@jira/router-resources__eoc-og-consolidation-user-state/src/consolidationUserStateResource.tsx';
import { getConsolidationStateResource } from '@jira/router-resources__eoc-tenant-info/src/getConsolidationStateResource.tsx';

import { gicSmartFieldsFeatureResource } from '@jira/router-resources__gic-smart-fields/src/useIsSmartFieldsOptIn.tsx';
import { gicOptInStatusResources } from '@jira/router-resources__global-issue-create-switch/src/gicOptInStatusResources.tsx';
import { issueTransitionChangeboardingPreferencesResource } from '@jira/router-resources__issue-transition-changeboarding-preferences/src/useIssueTransitionChangeboardingPreferences.tsx';
import { issueTransitionOptInStatusResource } from '@jira/router-resources__issue-transition-labs-opt-in/src/useIssueTransitionLabsOptIn.tsx';
import { navigationSidebarAutomationLegacyEnabledResource } from '@jira/router-resources__navigation-sidebar-automation/src/navigationSidebarAutomationLegacyEnabledResource.tsx';
import { navigationSidebarGlobalResource } from '@jira/router-resources__navigation-sidebar-global/src/navigationSidebarGlobalResource.tsx';
import { nav4SidebarSettingsPageSwitcherResource } from '@jira/router-resources__navigation-sidebar-settings-page-switcher/src/nav4SidebarSettingsPageSwitcherResource.tsx';
import { navigationUiStateResource } from '@jira/router-resources__navigation-ui-state/src/navigationUiStateResource.tsx';
import { persistentIssueCreateModalViewResource } from '@jira/router-resources__persistent-issue-create-modal-view/src/persistentIssueCreateModalViewResource.tsx';
import { projectContextResourceWithCache } from '@jira/router-resources__project-context/src/projectContextResource.tsx';
import { fetchSiteTraitsResource } from '@jira/router-resources__recommendations/src/fetchSiteTraitsResource.tsx';
import { fetchSiteUserTraitsResource } from '@jira/router-resources__recommendations/src/fetchSiteUserTraitsResource.tsx';
import { jpdRecommendationDismissedResource } from '@jira/router-resources__recommendations/src/jpdRecommendationDismissedResource.tsx';
import { rovoEntitlementResource } from '@jira/router-resources__rovo-entitlement/src/useRovoEntitlementResource.tsx';
import { userHomepageResource } from '@jira/router-resources__user-homepage/src/userHomepageResource.tsx';
import { resourceWithCondition } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';
import {
	postOfficeJiraSideNavResource,
	postOfficeNav4ProjectsSectionResource,
} from '@jira/router-resources__post-office/src/conditionalPostOfficeJiraTopBannerResource.tsx';
import type { RouteResource } from '@atlassian/react-resource-router/src/common/types.tsx';
import { previewPanelStateResource } from '@jira/navigation-apps__resources/src/services/preview-panel/index.tsx';
import { jiraSpaResources } from '@jira/router-resources__spa-page-container/src/jiraSpaResources.tsx';
import { navigationPlansVisibilityResource } from '@jira/portfolio__services-plans-item-visibility/src/useNavigationViewPlansPermission.tsx';
import { topNav4UIStateResource } from '@jira/navigation-apps__resources/src/services/top-nav/index.tsx';
import { navPlatformSidebarResource } from '@jira/nav-platform-integration__resources/src/navPlatformSidebarResource.tsx';
import {
	sidebarNav4Resource,
	sidebarEntryUiStateResource,
} from '@jira/navigation-apps__resources/src/services/sidebar-nav4/index.tsx';
import { staticProjectSettingsSidebarConnectAddonsNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar-settings-connect-addons/index.tsx';
import { staticPlanSidebarNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-plan-sidebar/index.tsx';
import { staticJSWUserBoardSidebarNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-jsw-userboard-sidebar/index.tsx';
import { forgeProjectSettingsModuleResource } from '@jira/navigation-apps__resources/src/controllers/forge/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';

import { navPlatformRootStateResource } from '@jira/platform__controllers-layout/src/controllers/layout-controller/containers/layout-manager/navPlatformRootStateResource.tsx';
import { getBannerResources } from './getConditionalBannerResources.tsx';

const conditionalPostOfficeJiraSideNav4Resource = resourceWithCondition(
	() => fg('post_office_ssr_jira_side_nav_enabled'),
	postOfficeJiraSideNavResource,
);

const conditionalPostOfficeJiraSideNav4ProjectsSectionResource = resourceWithCondition(
	() => fg('enable_ssr_recommendations_in_nav4'),
	postOfficeNav4ProjectsSectionResource,
);

const isModernIssueTransitionSettingEnabled = () => {
	return expVal('jira_issue_transition_screen_modernisation_toggle', 'value', 'OFF') !== 'OFF';
};

const conditionalGicOptInStatusResource = resourceWithCondition(
	() => fg('render_modern_global_issue_create_modal_experience'),
	gicOptInStatusResources,
);

const conditionalIssueTransitionOptInStatusResource = resourceWithCondition(
	isModernIssueTransitionSettingEnabled,
	issueTransitionOptInStatusResource,
);

export const getModernIssueTransitionResources = (): RouteResource[] => [
	conditionalIssueTransitionOptInStatusResource,
	issueTransitionChangeboardingPreferencesResource,
];

const getOperationsJiraSideNavResource = () => getConsolidationStateResource();

// There is a best effort duplicate removal in the `getRoutes` function but it
// only works if the resources have the same instance. b/c resourceWithCondition
// will create a new reference each time it is run, by keeping this outside the
// route factory, we can ensure that the duplicate removal works when it is used
// in multiple places.
const getDedupableConditionalResources = (isUsingSidebarEntrypoint?: boolean) => {
	const resources = [
		resourceWithCondition(() => fg('navx-global-side-nav'), navPlatformRootStateResource),
		sidebarEntryUiStateResource, // Note: this has an isSkipped: fg('navx-global-side-nav') check on it!
		topNav4UIStateResource,
		getOperationsJiraSideNavResource(),
	];
	if (isUsingSidebarEntrypoint) {
		// We don't want sidebarNav4Resource/navPlatformSidebarResource loaded for entrypoints otherwise
		// we end up with a client side api call when we don't need it as SSR will have loaded the data already.
		return resources;
	}
	resources.push(
		resourceWithCondition(
			() => fg('navx-global-side-nav'),
			navPlatformSidebarResource,
			sidebarNav4Resource,
		),
	);
	return resources;
};

type getNavigationResourcesOptions = {
	isUsingSidebarEntrypoint?: boolean;
};

export const getNavigationResources = ({
	isUsingSidebarEntrypoint,
}: getNavigationResourcesOptions = {}): RouteResource[] => [
	...jiraSpaResources(),
	resourceWithCondition(() => !fg('navx-global-side-nav'), navigationUiStateResource),
	navigationPlansVisibilityResource,
	userHomepageResource,
	conditionalGicOptInStatusResource,
	consolidationUserStateResource,
	...getDedupableConditionalResources(isUsingSidebarEntrypoint),
	...getModernIssueTransitionResources(),
	persistentIssueCreateModalViewResource,
	gicSmartFieldsFeatureResource,
	rovoEntitlementResource,
	jpdRecommendationDismissedResource,
	fetchSiteUserTraitsResource,
	fetchSiteTraitsResource,
	conditionalPostOfficeJiraSideNav4Resource,
	conditionalPostOfficeJiraSideNav4ProjectsSectionResource,
	...getBannerResources(),
	previewPanelStateResource,
];

export const getUserBoardNavigationResources = (): RouteResource[] => [
	staticJSWUserBoardSidebarNavigationResource,
];

export const getNavigationSidebarPlanResources = (): RouteResource[] => [
	staticPlanSidebarNavigationResource,
];

export const getNavigationSidebarProductsSettingResource = (): RouteResource[] => [
	navigationSidebarGlobalResource,
	getConsolidationStateResource(),
	checkApiKeyManagementKeysResource(),
	nav4SidebarGlobalProductsSettingsResource,
	nav4SidebarSettingsPageSwitcherResource,
];

export const getNavigationSidebarProjectSettingsResource = (): RouteResource[] => [
	navigationSidebarAutomationLegacyEnabledResource,
	projectContextResourceWithCache,
	forgeProjectSettingsModuleResource,
	staticProjectSettingsSidebarConnectAddonsNavigationResource,
];

// TODO : In future, we will remove this and only use getNavigationResources, once the Navigation team refactors and trims down the resources
export const getNavigationResourcesForWorkItem = (): RouteResource[] => [
	...jiraSpaResources(),
	// Checks if the Plans menu should appear in the sidebar
	navigationPlansVisibilityResource,
	// check if we can render the global issue create modal when clicking the Create button.
	conditionalGicOptInStatusResource,
	// ConversationAssistant component on the TopNav
	rovoEntitlementResource,
	// used in Nav4JiraSideNavRecommendation
	conditionalPostOfficeJiraSideNav4Resource,
	conditionalPostOfficeJiraSideNav4ProjectsSectionResource,
	// used in glance panel.
	previewPanelStateResource,
	// issues related resources -> issue-create modal
	persistentIssueCreateModalViewResource,
	gicSmartFieldsFeatureResource,
	...getModernIssueTransitionResources(),
	...getDedupableConditionalResources(false),
	...getBannerResources(),
];
